<template>
  <div>
    <div class="hero mb-5 d-flex">
      <div class="content container m-auto text-center">
        <h1>Customer Installation FAQ Guide</h1>
        <h5>Helping You Get Started Smoothly</h5>
      </div>
    </div>

    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div
            class="faq-section"
            v-for="(section, sIdx) in sections"
            :key="sIdx"
          >
            <div class="faq-section-header">
              <i :class="section.icon"></i>
              <h4>{{ section.title }}</h4>
            </div>
            <div class="faq-body">
              <div
                class="faq-item"
                v-for="(faq, fIdx) in section.items"
                :key="sIdx + '-' + fIdx"
              >
                <div
                  class="faq-question"
                  :class="{ active: openKey === sIdx + '-' + fIdx }"
                  @click="toggle(sIdx + '-' + fIdx)"
                >
                  <span>{{ faq.q }}</span>
                  <i
                    class="fas"
                    :class="openKey === sIdx + '-' + fIdx ? 'fa-minus' : 'fa-plus'"
                  ></i>
                </div>
                <transition name="faq-slide">
                  <div class="faq-answer" v-if="openKey === sIdx + '-' + fIdx">
                    <div v-html="faq.a"></div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="faq-notice">
            <div class="notice-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div>
              <h5>Important Notes</h5>
              <ul>
                <li>Always keep the router powered on.</li>
                <li>Do not reset the router unless advised by our support team.</li>
              </ul>
              <p class="notice-disclaimer">
                If you have any questions or need further clarification, please
                reach out to us via our
                <a href="https://facebook.com/TridentNetwork" target="_blank">Facebook Page</a>
                or our hotline at <strong>0960 934 0459</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="cta-section">
      <div class="container text-center">
        <h2>Still have questions?</h2>
        <p>
          Our team is happy to help. Reach out and we'll get back to you as
          soon as possible.
        </p>
        <div class="cta-contacts">
          <a href="tel:09609340459" class="btn btn-secondary btn-lg">
            <i class="fas fa-phone-alt me-2"></i> 0960 934 0459
          </a>
          <a href="https://facebook.com/TridentNetwork" target="_blank" class="btn second-button btn-lg ms-3">
            <i class="fab fa-facebook me-2"></i> Facebook Page
          </a>
        </div>
        <p class="cta-hours">
          Monday to Saturday: 9:00 AM - 6:00 PM
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  title: "Trident Network - Frequently Asked Questions",
  data() {
    return {
      openKey: null,
      sections: [
        {
          title: "General Questions",
          icon: "fas fa-thumbtack",
          items: [
            {
              q: "How much is my deposit?",
              a: "The deposit amount is equal to the monthly fee of the plan you choose to subscribe to. For example, if your monthly plan is &#8369;1,000, then your deposit will also be &#8369;1,000."
            },
            {
              q: "What is the deposit, and when can I use it? Is it refundable?",
              a: "The deposit can only be used in your final month if you choose to disconnect our service. During your regular billing cycle, the deposit cannot be used to pay your monthly fees. It is non-refundable and will remain unused unless you decide to disconnect."
            },
            {
              q: "Is installation free?",
              a: "Yes! Installation is completely free. You won't be charged any setup fee."
            }
          ]
        },
        {
          title: "Plans & Billing",
          icon: "fas fa-cog",
          items: [
            {
              q: "What internet plans are available?",
              a: ""
            },
            {
              q: "When is the due date for monthly payment?",
              a: "Your bill is always due on the <strong>1st of every month</strong>."
            },
            {
              q: "What happens if my installation date is not the 1st?",
              a: `We apply <strong>prorated billing</strong> for your first month. You only pay for the remaining days of the month.
              <div class="faq-example">
                <strong>Example:</strong> Installed on March 15 with &#8369;999 plan<br>
                March 15: Pay &#8369;999 deposit<br>
                April 1: Pay only for March 15&ndash;31<br>
                &rarr; &#8369;999 &divide; 31 &times; 17 days = <strong>&#8369;547.74</strong><br>
                May 1 onward: Pay full &#8369;999 monthly
              </div>`
            },
            {
              q: "What happens if I disconnect the service?",
              a: "Your 1-month deposit will be used to cover your final month. You don't need to pay anything else, but the deposit is not refundable.<br><br>If you wish to disconnect, kindly return the modem to us as it remains our property due to the free installation. If you are renting the house, please contact us so we can arrange for the collection of the modem."
            },
            {
              q: "What happens if I miss the payment due date?",
              a: "The disconnection date is every <strong>5th of the month</strong>. If we do not receive any payment before the 5th, your internet connection will be automatically disconnected. Even if you make the payment after the cutoff, your billing amount for the month will remain the same.<br><br>For example, if your monthly plan is &#8369;999 and you miss the cutoff in April and pay on the 10th, your billing amount will still be &#8369;999 for May. We encourage you to pay on time to avoid disconnection."
            },
            {
              q: "What should I do after making a payment?",
              a: `After you've completed your payment, please send the following details to our Facebook page:
              <ul class="mt-2">
                <li><strong>Account Name:</strong> [Your Name]</li>
                <li><strong>Reference Number:</strong> (e.g., GCASH) 12312312</li>
                <li><strong>Screenshot of the Receipt:</strong> From your payment app (GCash, Maya, etc.)</li>
              </ul>
              <p class="mt-2">If you paid through our <strong>CRM Online</strong>, you can skip these steps because the payment will be automatically recorded in our system.</p>`
            }
          ]
        },
        {
          title: "Support & Troubleshooting",
          icon: "fas fa-headset",
          items: [
            {
              q: "What is my Wi-Fi name and password?",
              a: "You can find your Wi-Fi name and password on the label located <strong>under your modem/router</strong>."
            },
            {
              q: "Can I change my Wi-Fi name/password later?",
              a: "Yes, you can change your Wi-Fi name and password at any time. However, we highly recommend <strong>not changing the password</strong> unless you notice slower speeds or suspect many people are connected and know your password."
            },
            {
              q: "What should I do if I experience slow speed or LOS (Loss of Signal)?",
              a: `If you are experiencing slow internet speed or LOS, please follow these steps:
              <ol class="mt-2">
                <li>Restart your router by unplugging it for 10 seconds and plugging it back in.</li>
                <li>Check the lights on your router. If the LOS light is <strong>red or blinking</strong>, it means there's a signal issue.</li>
                <li>If the problem continues, please report it to us immediately by calling or texting our hotline.</li>
              </ol>
              <p class="mt-2">We will schedule a repair and resolve the issue within <strong>24&ndash;48 hours</strong>.</p>`
            },
            {
              q: "Can I move the modem or add a LAN cable?",
              a: "Yes, you can move the modem, but <strong>be careful with the fiber optic cable</strong> as it is fragile. Avoid bending or putting pressure on it to prevent damage.<br><br>You can also add a LAN cable to the modem to connect other devices directly. This will allow you to enjoy faster speeds and lower latency, providing a more stable connection for activities like gaming or streaming."
            },
            {
              q: "How can I contact support?",
              a: `You can reach our support team through the following channels:
              <ul class="mt-2">
                <li><i class="fas fa-phone-alt"></i> <strong>Call or Text:</strong> 0960 934 0459</li>
                <li><i class="fab fa-facebook"></i> <strong>Facebook Page:</strong> Trident Network</li>
              </ul>
              <p class="mt-2"><strong>Working Hours:</strong><br>Monday to Saturday: 9:00 AM &ndash; 6:00 PM<br>For emergencies, we can make exceptions outside of these hours.</p>`
            }
          ]
        }
      ]
    };
  },
  created() {
    const fiber = this.$store.state.fiberPlans;
    const wireless = this.$store.state.wirelessPlans;
    const fiberList = fiber.map(p => '<li><strong>\u20B1' + p.price + '</strong> \u2014 Up to ' + p.speed + '</li>').join('');
    const wirelessList = wireless.map(p => '<li><strong>\u20B1' + p.price + '</strong> \u2014 Up to ' + p.speed + '</li>').join('');
    const answer = '<strong>Fiber Plans</strong><ul class="plan-list">' + fiberList + '</ul><strong>Wireless Plans</strong><ul class="plan-list">' + wirelessList + '</ul><p>All plans come with unlimited data and no lock-in contracts!</p><a href="/#plans">View Broadband Plans \u2192</a>';
    this.sections[1].items[0].a = answer;
  },
  methods: {
    toggle(key) {
      this.openKey = this.openKey === key ? null : key;
    }
  },
  mounted() {
    window.scrollTo(0, 0);
  }
};
</script>

<style lang="scss" scoped>
.faq-section {
  margin-bottom: 40px;
}

.faq-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #242424;

  i {
    font-size: 20px;
    color: #00ffd0;
  }

  h4 {
    margin-bottom: 0;
    font-size: 22px;
    font-weight: 700;
  }
}

.faq-body {
  padding: 0;
}

.faq-item {
  margin-bottom: 8px;
  border-radius: 10px;
  overflow: hidden;
  background: #131516;
  border: 1px solid #242424;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #333;
  }
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  transition: background 0.2s ease;

  span {
    font-size: 16px;
    font-weight: 600;
    color: #fff;
  }

  i {
    color: #00ffd0;
    font-size: 14px;
    flex-shrink: 0;
    margin-left: 16px;
  }

  &:hover {
    background: #1c2225;
  }

  &.active {
    background: #1c2225;
  }
}

.faq-answer {
  padding: 16px 24px 20px;
  color: #d0d0d0;
  font-size: 15px;
  line-height: 1.8;

  ul, ol {
    padding-left: 20px;
    margin-bottom: 8px;

    li {
      margin-bottom: 6px;
    }
  }

  i {
    color: #00ffd0;
    margin-right: 6px;
    width: 18px;
  }

  code {
    color: #00ffd0;
    background: #1c2225;
    padding: 2px 8px;
    border-radius: 4px;
  }

  strong {
    color: #fff;
  }

  p {
    margin-bottom: 4px;
  }
}

::v-deep .faq-example {
  margin-top: 12px;
  padding: 16px;
  background: #1c2225;
  border-radius: 8px;
  border-left: 3px solid #00ffd0;
  font-size: 14px;
  line-height: 1.8;
}

::v-deep .plan-list {
  list-style: none;
  padding: 0;

  li {
    padding: 8px 0;
    border-bottom: 1px solid #242424;
    font-size: 16px;

    &:last-child {
      border-bottom: none;
    }
  }
}

.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: all 0.25s ease;
  max-height: 800px;
  overflow: hidden;
}

.faq-slide-enter,
.faq-slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.faq-notice {
  display: flex;
  gap: 20px;
  background: #131516;
  border: 1px solid #242424;
  border-radius: 10px;
  padding: 24px;
  margin-top: 20px;
  margin-bottom: 20px;

  .notice-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #1c2225;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 20px;
      color: #00ffd0;
    }
  }

  h5 {
    font-size: 18px;
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 12px;

    li {
      color: #d0d0d0;
      font-size: 15px;
      margin-bottom: 4px;
    }
  }

  .notice-disclaimer {
    color: #999;
    font-size: 14px;
    margin-bottom: 0;

    a {
      color: #00ffd0;
    }

    strong {
      color: #fff;
    }
  }
}

.cta-section {
  padding: 80px 0;
  border-top: 1px solid #242424;

  h2 {
    margin-bottom: 12px;
  }

  p {
    color: #b0b0b0;
    font-size: 18px;
    margin-bottom: 28px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-contacts {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }

  .cta-hours {
    color: #666;
    font-size: 14px;
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .faq-question span {
    font-size: 15px;
  }

  .faq-answer {
    padding: 12px 16px 16px;
  }

  .faq-notice {
    flex-direction: column;
    gap: 12px;
  }

  .cta-contacts {
    flex-direction: column;
    align-items: center;

    .btn {
      margin-left: 0 !important;
      width: 100%;
      max-width: 300px;
    }
  }
}
</style>
